import { isListNotEmpty, isObjectNotEmpty, isObjectEmpty, isStringNotEmpty, isListEmpty, isListLongerThanOne } from './objectUtils';

export const getProductDisplayName = (product) => {
	if (!isObjectNotEmpty(product)) return '';

	// metaData rows are authored in an admin grid, so a blank row can arrive as a null
	// entry; the dose-line reader guards the same field the same way.
	const alternativeName = product?.metaData?.find((item) => item?.key === 'ALTERNATIVE_NAME')?.value;

	if (isStringNotEmpty(alternativeName)) return alternativeName;

	// always a string: callers interpolate this straight into labels, and a product with
	// no name would otherwise print the word "undefined"
	return product?.name ?? '';
};

export const findDisplayProduct = ({ productId, productSku, lineItemSku } = {}, products = []) => {
	if (!isListNotEmpty(products)) return null;

	if (isStringNotEmpty(productId)) {
		const product = products.find((item) => item?.id === productId);
		if (isObjectNotEmpty(product)) return product;
	}

	const sku = productSku || lineItemSku;
	if (isStringNotEmpty(sku)) {
		const product = products.find((item) => item?.sku === sku);
		if (isObjectNotEmpty(product)) return product;
	}

	return null;
};

// ── Treatment label / variant parsing ──
// Shared by the TreatmentCard rows and the Prescription/Subscription detail
const DOSAGE_BOUNDARY_PATTERN = /\b\d+(?:\.\d+)?\s*(?:mg|mcg|g|ml|iu|units?|%)\b/i;

const normalizeLabel = (value) => (isStringNotEmpty(value) ? value.replace(/\s+/g, ' ').trim() : null);

const withoutPrescriptionSuffix = (value) => normalizeLabel(value)?.replace(/\s+prescription$/i, '') || null;

// Splits a raw label like "Glutathione 100mg injection 2000mg" into
// { name: "Glutathione", variant: "100mg injection 2000mg" } by cutting at the
// first dosage token. Labels with no dosage return the whole string as the name.
export const splitTreatmentLabel = (value) => {
	const normalizedValue = withoutPrescriptionSuffix(value);
	if (!isStringNotEmpty(normalizedValue)) return { name: null, variant: null };

	const dosageMatch = normalizedValue.match(DOSAGE_BOUNDARY_PATTERN);
	if (!dosageMatch || dosageMatch.index <= 0) {
		return { name: normalizedValue, variant: null };
	}

	return {
		name: withoutPrescriptionSuffix(normalizedValue.slice(0, dosageMatch.index)),
		variant: normalizeLabel(normalizedValue.slice(dosageMatch.index)),
	};
};

export const getTreatmentDisplayName = ({
	product,
	productId,
	productSku,
	lineItemSku,
	candidateNames = [],
	fallback = 'Treatment',
} = {}, products = []) => {
	const displayProduct = isObjectNotEmpty(product)
		? product
		: findDisplayProduct({ productId, productSku, lineItemSku }, products);

	const productDisplayName = getProductDisplayName(displayProduct);
	if (isStringNotEmpty(productDisplayName)) return productDisplayName;

	// a bare name carries no catalog metadata, so it can only be shown as written; the
	// patient-facing label is set by ALTERNATIVE_NAME on the product record above
	for (const candidateName of candidateNames) {
		if (isStringNotEmpty(candidateName)) return candidateName;
	}

	return fallback;
};

/**
 * Resolves the display product name for a subscription using ReadyRx product
 * display utilities and available line-item fallbacks.
 *
 * @param {object} subscription - Subscription to label.
 * @param {Array<object>} [products=[]] - Product records for display-name resolution.
 * @param {object} [options]
 * @param {string} [options.fallback='Treatment'] - Label when nothing resolves. Pass
 *   an empty string when the caller needs to distinguish "unresolved" from a real name.
 * @returns {string} Product display name.
 */
export const getSubscriptionDisplayProductName = (subscription, products = [], { fallback = 'Treatment' } = {}) => (
	getTreatmentDisplayName({
		productId: subscription?.productId,
		productSku: subscription?.productSku,
		lineItemSku: subscription?.lineItems?.[0]?.sku,
		candidateNames: [
			subscription?.productName,
			subscription?.lineItems?.[0]?.name,
		],
		fallback,
	}, products)
);

// An encounter carries no product identity of its own, so the subscription it was
// created from is the only treatment anchor available. Every CONSULT and FOLLOWUP
// creation path stamps subscriptionIds, so the join holds for both visit types.
/**
 * Resolves the treatment name behind an encounter, matching the name shown on the
 * patient's treatment card for the same subscription.
 *
 * @param {object} encounter - Encounter to label.
 * @param {object} [input]
 * @param {Array<object>} [input.subscriptions=[]] - Subscriptions to match against.
 * @param {Array<object>} [input.products=[]] - Product records for display-name resolution.
 * @returns {string} Treatment display name, or an empty string when unresolvable.
 */
export const getEncounterTreatmentName = (encounter, { subscriptions = [], products = [] } = {}) => {
	if (isListEmpty(encounter?.subscriptionIds) || isListEmpty(subscriptions)) return '';

	const matched = subscriptions.filter((item) => encounter.subscriptionIds.includes(item?.id));
	if (isObjectEmpty(matched[0])) return '';
	// every creation path stamps a single id today, but the field is plural: naming one
	// treatment for a visit that covers several would tell the patient the others are fine
	if (isListLongerThanOne(matched)) return '';

	return getSubscriptionDisplayProductName(matched[0], products, { fallback: '' });
};
